import type {
  ModifierApplyAction,
  ModifierRequest,
} from "@v2/features/modifiers/store/ModifierQueueStoreContext";
import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import React, { useCallback, useEffect } from "react";

import { analytics } from "@v2/features/infra/analytics/tracking";
import { useIframeMessaging } from "@v2/features/modifiers/hooks/useIframeMessaging";
import {
  useIsProcessing,
  useModifierQueueActions,
  useModifierQueueContext,
  useQueue,
  useWaitingForCleanup,
} from "@v2/features/modifiers/store/ModifierQueueStoreContext";
import { getCssPropertiesFromModifier } from "@v2/features/modifiers/utils";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useErrorToast } from "@v2/hooks/useErrorToast";
import { modifiersClient } from "@v2/trpc/routes/modifiers";
import {
  nextJSProjectClient,
  nextJSProjectUtils,
} from "@v2/trpc/routes/nextJSProject";
import { useSearchParams } from "react-router-dom";
import { ReploError } from "schemas/errors";
import { COMPUTE_VALUE } from "schemas/fileModifiers";

/**
 * NOTE (Kevin, 2025-12-22, REPL-21177): Check if a modifier has a __COMPUTE__ value.
 * These are sent to the iframe for resolution but not enqueued to the server.
 */
function isComputeModifier(modifier: UniversalModifierValue): boolean {
  return (modifier as { value?: unknown }).value === COMPUTE_VALUE;
}

class ModifierParameterError extends ReploError {}

type OptimisticUpdatePayloadBase = {
  timestamp: number;
  stableElementId: string;
};

type OptimisticUpdatePayload =
  | (OptimisticUpdatePayloadBase & {
      modifierType: "asset";
      assetSource: string;
    })
  | (OptimisticUpdatePayloadBase & {
      modifierType: "content";
      content: string;
      className: string;
    })
  | (OptimisticUpdatePayloadBase & {
      modifierType: "href";
      hrefValue: string;
    })
  | (OptimisticUpdatePayloadBase & {
      modifierType: Exclude<
        UniversalModifierValue["type"],
        "asset" | "content" | "href"
      >;
      /**
       * NOTE (Kevin, 2025-12-10): Inner null allows individual CSS properties to be set
       * to null for "auto"/removal. Outer null means no CSS changes for this update.
       */
      cssProperties: Record<string, string | null> | null;
    });

export function useModifiers() {
  const projectId = useCurrentProjectId();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const errorToast = useErrorToast();

  const { sendMessage } = useIframeMessaging();

  const { store } = useModifierQueueContext();
  const queue = useQueue();
  const isProcessing = useIsProcessing();
  const waitingForCleanup = useWaitingForCleanup();
  const actions = useModifierQueueActions();

  /**
   * NOTE (Kurt, 2025-11-21): Cleanup optimistic updates after Fast Refresh completes.
   *
   * Flow:
   * 1. User changes style → applyModifier() sends APPLY_OPTIMISTIC_UPDATE to iframe
   * 2. Iframe applies CSS inline immediately (optimistic update)
   * 3. Modifier queued → processNextRequest() sends to server via modifyBatch.mutate
   * 4. Server updates file and returns fileTree → mountAdditionalFiles() writes to WebContainer
   * 5. Next.js dev server detects file change → compiles and triggers Fast Refresh
   * 6. Fast Refresh logs to console → iframe's console.log intercept sends FAST_REFRESH_COMPLETE
   * 7. useIframeMessageRouter receives FAST_REFRESH_COMPLETE → calls performCleanup()
   * 8. performCleanup() sends REMOVE_OPTIMISTIC_UPDATES with pending updates to reapply
   * 9. Iframe removes old inline styles and reapplies pending ones
   * 10. setWaitingForCleanup(false) → allows next queued request to process
   *
   * This ensures optimistic updates are cleaned up after Fast Refresh bakes styles into
   * the DOM via Tailwind classes, while preserving any pending updates in the queue.
   */

  const performCleanup = useCallback(() => {
    const state = store.getState();

    if (!state.waitingForCleanup || !state.lastProcessedRequest) {
      return;
    }

    const pendingOptimisticUpdates = state.queue.flatMap((request) => {
      return request.actions.map((action) => {
        // NOTE (Kurt, 2025-12-11): Handle different modifier types for cleanup
        if (action.modifier.type === "asset") {
          return {
            stableElementId: action.stableElementId,
            modifier: action.modifier,
            cssProperties: null,
            content: null,
            timestamp: request.timestamp,
          };
        }
        if (action.modifier.type === "content") {
          return {
            stableElementId: action.stableElementId,
            modifier: action.modifier,
            content: action.modifier.value,
            className: action.modifier.className,
            cssProperties: null,
            timestamp: request.timestamp,
          };
        }
        if (action.modifier.type === "href") {
          return {
            stableElementId: action.stableElementId,
            modifier: action.modifier,
            cssProperties: null,
            content: null,
            timestamp: request.timestamp,
          };
        }
        const cssProperties = getCssPropertiesFromModifier(action.modifier);
        return {
          stableElementId: action.stableElementId,
          modifier: action.modifier,
          cssProperties,
          content: null,
          timestamp: request.timestamp,
        };
      });
    });

    const removeOptimisticUpdatesPayload = {
      modifierBatchCreatedTimestamp: state.lastProcessedRequest.timestamp,
      updatesToSync: pendingOptimisticUpdates,
    };

    sendMessage({
      type: "REMOVE_OPTIMISTIC_UPDATES",
      payload: removeOptimisticUpdatesPayload,
    });

    sendMessage({
      type: "REFRESH_SELECTED_ELEMENT",
      payload: {},
    });

    state.actions.setWaitingForCleanup(false);
  }, [store, sendMessage]);

  const processNextRequest = useCallback(async () => {
    const state = store.getState();
    const nextRequest = state.queue[0];

    // NOTE (Kurt, 2025-11-21): Don't process next request if we're waiting for cleanup from the previous request
    if (!nextRequest || state.isProcessing || state.waitingForCleanup) {
      return;
    }

    actions.setIsProcessing(true);

    const requestPayload = {
      projectId: nextRequest.projectId,
      fileId: nextRequest.pageId,
      actions: nextRequest.actions.map((action) => ({
        elementSelector: {
          stableElementId: action.stableElementId,
        },
        modifier: action.modifier,
      })),
    };

    try {
      await modifiersClient.modifyBatch.mutate(requestPayload);

      analytics.logEvent("editor.editMode.modifyBatch", {
        fileId: nextRequest.pageId,
        modifierTypes: nextRequest.actions.map((a) => a.modifier.type),
        actionCount: nextRequest.actions.length,
      });

      /**
       * NOTE (Kurt, 2025-08-25): For font family changes, we need to update
       * globals.css separately to include the new font definitions. This
       * ensures the new font is available immediately without requiring a
       * full container reload.
       */
      const hasFontFamilyModifier = nextRequest.actions.some(
        (action) => action.modifier.type === "fontFamily",
      );

      if (hasFontFamilyModifier) {
        try {
          // NOTE (Bobby, 2025-11-19): We no longer need to mount the additional files that this returns
          // but this endpoint is still responsible for updating the sandbox so we need this. Need to move
          // functions like this from the webContainerClient into somewhere else.
          await nextJSProjectClient.getGlobalsCssFileTree.query({
            projectId: nextRequest.projectId,
          });
        } catch (error) {
          console.warn("Failed to mount globals CSS file tree:", error);
        }
      }

      await nextJSProjectUtils.getPage.invalidate({
        projectId: nextRequest.projectId,
        id: nextRequest.pageId,
      });

      actions.setLastProcessedRequest(nextRequest);
      actions.setWaitingForCleanup(true);

      // NOTE (Kurt, 2025-11-24): Fallback timeout to unblock queue if Fast Refresh never completes.
      // This can happen if the file change doesn't trigger HMR or if Fast Refresh is slow.
      // After 5 seconds, we'll force cleanup to allow the queue to continue processing.
      setTimeout(() => {
        const currentState = store.getState();
        if (currentState.waitingForCleanup) {
          performCleanup();
        }
      }, 5000);
    } catch (error) {
      console.error(
        "[useModifiers] Error processing modifier request:",
        JSON.stringify({
          error: error instanceof Error ? error.message : String(error),
          requestPayload,
        }),
      );
      errorToast(
        "Your style change could not be applied. Please use Replo Chat!",
        "Style modification failed",
        "error.element.update",
        {
          error: error instanceof Error ? error.message : String(error),
        },
      );
    } finally {
      actions.setIsProcessing(false);
      actions.dequeue();
    }
  }, [store, actions, errorToast, performCleanup]);

  useEffect(() => {
    if (!isProcessing && !waitingForCleanup && queue.length > 0) {
      void processNextRequest();
    }
  }, [queue, isProcessing, waitingForCleanup, processNextRequest]);

  const applyModifier = useCallback(
    ({
      modifier,
      stableElementId,
    }: {
      modifier: UniversalModifierValue;
      stableElementId: string;
    }) => {
      if (!projectId || !pageId) {
        throw new ModifierParameterError({
          message: "Missing required parameters for modifier",
          additionalData: {
            projectId,
            pageId,
          },
        });
      }

      const timestamp = Date.now();
      let payload: OptimisticUpdatePayload | null = null;

      if (modifier.type === "asset") {
        payload = {
          modifierType: "asset",
          assetSource: modifier.value,
          timestamp,
          stableElementId,
        };
      } else if (modifier.type === "content") {
        // NOTE (Kurt, 2025-12-11): Content modifiers update text and formatting classes
        payload = {
          modifierType: "content",
          content: modifier.value,
          className: modifier.className ?? "",
          timestamp,
          stableElementId,
        };
      } else if (modifier.type === "href") {
        payload = {
          modifierType: "href",
          hrefValue: modifier.value,
          timestamp,
          stableElementId,
        };
      } else {
        const cssProperties = getCssPropertiesFromModifier(modifier);
        if (cssProperties) {
          payload = {
            modifierType: modifier.type,
            cssProperties,
            timestamp,
            stableElementId,
          };
        }
      }

      if (payload) {
        sendMessage({
          type: "APPLY_OPTIMISTIC_UPDATE",
          payload,
        });
      }

      // NOTE (Kevin, 2025-12-22, REPL-21177): __COMPUTE__ modifiers are only sent to
      // the iframe for resolution. They're not enqueued - the iframe will send back
      // COMPUTED_STYLES_RESOLVED with the actual value, which gets enqueued then.
      if (isComputeModifier(modifier)) {
        return;
      }

      const newAction: ModifierApplyAction = {
        modifier,
        stableElementId,
      };

      const newRequest: ModifierRequest = {
        projectId,
        pageId,
        actions: [newAction],
        timestamp,
      };

      actions.enqueue(newRequest);
    },
    [projectId, pageId, sendMessage, actions],
  );

  return { applyModifier, isProcessing, performCleanup };
}
